/**
 * Content Metrics
 * ---------------------------------------------------------------
 * Atomic mutation helpers for `content_metrics`. Wraps the
 * `record_content_event` RPC so every increment also lands a
 * row in `content_events` for the audit trail.
 *
 * Pure operational telemetry — no UI, no formatting.
 */

import { supabase } from "@/integrations/supabase/client";

export type CounterMetric =
  | "views"
  | "likes"
  | "comments"
  | "shares"
  | "saves"
  | "impressions"
  | "copied_responses"
  | "exports"
  | "publish_count";

export interface AssetMetricsRow {
  content_asset_id: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  impressions: number;
  watch_time: number;
  completion_rate: number;
  copied_responses: number;
  exports: number;
  publish_count: number;
  engagement_score: number;
}

const EVENT_FOR_METRIC: Record<CounterMetric, string> = {
  views: "view",
  likes: "like",
  comments: "comment",
  shares: "share",
  saves: "save",
  impressions: "impression",
  copied_responses: "copy",
  exports: "export",
  publish_count: "publish",
};

/** Atomically increment a single counter on content_metrics. */
export async function incrementMetric(
  assetId: string,
  metric: CounterMetric,
  amount = 1,
  metadata?: Record<string, unknown>
): Promise<void> {
  if (!assetId) return;
  void (async () => {
    try {
      await supabase.rpc("record_content_event" as any, {
        p_content_asset_id: assetId,
        p_event_type: EVENT_FOR_METRIC[metric],
        p_metric_column: metric,
        p_increment: amount,
        p_timestamp_ms: null,
        p_metadata: (metadata ?? null) as any,
      });
    } catch (e) {
      console.error("[contentMetrics] increment failed", metric, e);
    }
  })();
}

export const trackAssetView = (assetId: string) =>
  incrementMetric(assetId, "views");

export const trackAssetExport = (assetId: string) =>
  incrementMetric(assetId, "exports");

export const trackAssetShare = (assetId: string, channel?: string) =>
  incrementMetric(assetId, "shares", 1, channel ? { channel } : undefined);

/**
 * Engagement Score (0–100).
 * Weighted combination of high-signal behaviors + completion rate.
 */
export function calculateEngagementScore(m: Partial<AssetMetricsRow>): number {
  const shares = m.shares ?? 0;
  const saves = m.saves ?? 0;
  const copies = m.copied_responses ?? 0;
  const exports = m.exports ?? 0;
  const completion = Math.max(0, Math.min(100, Number(m.completion_rate ?? 0)));

  const raw =
    shares * 3 +
    saves * 2 +
    copies * 4 +
    exports * 2 +
    completion;

  // Soft-normalize: treat ~150 as a strong signal ceiling.
  const normalized = Math.round(Math.min(100, (raw / 150) * 100));
  return Number.isFinite(normalized) ? normalized : 0;
}

export type EngagementBand = "low" | "medium" | "high";

export function engagementBand(score: number): EngagementBand {
  if (score >= 65) return "high";
  if (score >= 30) return "medium";
  return "low";
}

/**
 * Deterministic, asset-id-derived synthetic metrics for assets that
 * have not yet accumulated real telemetry. Keeps the admin console
 * looking operational while the system warms up. ZERO randomness on
 * subsequent reads — same asset always returns the same baseline.
 */
export function syntheticBaseline(assetId: string): Partial<AssetMetricsRow> {
  let h = 0;
  for (let i = 0; i < assetId.length; i++) {
    h = (h * 31 + assetId.charCodeAt(i)) >>> 0;
  }
  const r = (mod: number) => h % mod;
  const impressions = 120 + r(880);
  const views = Math.floor(impressions * (0.35 + (r(40) / 100)));
  const watch_time = Math.round(views * (3 + r(9)));
  const completion_rate = 28 + r(60);
  const shares = Math.floor(views * (0.01 + r(5) / 200));
  const saves = Math.floor(views * (0.02 + r(7) / 200));
  const copied_responses = Math.floor(views * (0.005 + r(3) / 300));
  const exports = r(6);
  const publish_count = r(3);

  return {
    impressions,
    views,
    watch_time,
    completion_rate,
    shares,
    saves,
    likes: Math.floor(views * 0.06),
    comments: Math.floor(views * 0.012),
    copied_responses,
    exports,
    publish_count,
  };
}
